import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * REEL 001 — a silent 90-second cinematic scroll study.
 * Nine chapters, scroll-driven parallax, editorial captions.
 * Ends with two CTAs to convert.
 */

function useMedia() {
  const [media, setMedia] = useState({});
  useEffect(() => {
    axios.get(`${API}/media/services`).then((r) => {
      const raw = r.data.media || {};
      const base = process.env.REACT_APP_BACKEND_URL;
      const out = {};
      for (const [k, v] of Object.entries(raw)) {
        out[k] = v.startsWith("http") ? v : `${base}${v}`;
      }
      setMedia(out);
    }).catch(() => {});
  }, []);
  return media;
}

/* ----- Frame primitives ---------------------------------------------- */

function Chapter({ children, id }) {
  return (
    <section
      className="relative h-screen w-full overflow-hidden flex items-center"
      data-testid={`reel-chapter-${id}`}
    >
      {children}
    </section>
  );
}

function ImageLayer({ src, opacity, scale, y }) {
  return (
    <motion.div
      className="absolute inset-0"
      style={{ opacity, scale, y }}
      aria-hidden
    >
      {src && (
        <img
          src={src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      )}
      {/* Cinematic vignette */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(circle at 50% 40%, transparent 30%, rgba(0,0,0,0.6) 90%)"
      }} />
      <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.85) 100%)"
      }} />
    </motion.div>
  );
}

/* ----- Individual chapters ------------------------------------------ */

function ColdOpen() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const op = useTransform(scrollYProgress, [0, 0.7, 1], [1, 1, 0]);
  return (
    <div ref={ref} className="w-full">
      <Chapter id="cold-open">
        <motion.div style={{ y, opacity: op }} className="w-full max-w-[1400px] mx-auto px-6 md:px-14 relative z-10">
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.44em]" style={{ color: "var(--gold)" }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--gold)", boxShadow: "0 0 12px var(--gold)" }} />
            Reel 001
          </div>
          <h1 className="mt-10 font-display leading-[0.92]" style={{ fontSize: "clamp(3.5rem, 10vw, 11rem)", letterSpacing: "-0.04em" }}>
            A ninety-second<br />
            <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>study.</span>
          </h1>
          <p className="mt-12 max-w-xl font-body text-lg md:text-xl" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
            No music. No voice-over. Just the work, the crew, and the standard we hold ourselves to on every property.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 1.2 }}
          className="absolute bottom-14 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10"
        >
          <div className="font-mono text-[9px] uppercase tracking-[0.5em]" style={{ color: "var(--text-3)" }}>Scroll</div>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4 }}>
            <ArrowDown size={14} style={{ color: "var(--gold)" }} />
          </motion.div>
        </motion.div>
      </Chapter>
    </div>
  );
}

function StillFrame({ image, chapter, kicker, headline, subline, id, reverse = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1.02]);
  const imgY     = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const imgOp    = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const textY    = useTransform(scrollYProgress, [0, 0.5, 1], [80, 0, -80]);
  const textOp   = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  return (
    <div ref={ref} className="w-full">
      <Chapter id={id}>
        <ImageLayer src={image} opacity={imgOp} scale={imgScale} y={imgY} />
        <motion.div
          style={{ y: textY, opacity: textOp }}
          className={`relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-14 flex ${reverse ? "justify-end" : "justify-start"}`}
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>
              <div className="h-px w-10" style={{ background: "var(--gold)" }} />
              <span>{chapter}</span>
              <span style={{ color: "var(--text-3)" }}>· {kicker}</span>
            </div>
            <h2 className="mt-8 font-display leading-[0.95]" style={{ fontSize: "clamp(2.6rem, 6.5vw, 6rem)", letterSpacing: "-0.03em" }}>
              {headline}
            </h2>
            <p className="mt-8 font-body text-lg md:text-xl max-w-lg" style={{ color: "var(--text-2)", lineHeight: 1.65 }}>
              {subline}
            </p>
          </div>
        </motion.div>
      </Chapter>
    </div>
  );
}

function StatFrame({ image, chapter, kicker, stats, id }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.2, 1.0]);
  const imgOp    = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return (
    <div ref={ref} className="w-full">
      <Chapter id={id}>
        <ImageLayer src={image} opacity={imgOp} scale={imgScale} y={0} />
        <div className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-14">
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>
            <div className="h-px w-10" style={{ background: "var(--gold)" }} />
            <span>{chapter}</span>
            <span style={{ color: "var(--text-3)" }}>· {kicker}</span>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-20 max-w-5xl">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.4 }}
                transition={{ duration: 1.1, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="font-display leading-[0.95]" style={{ fontSize: "clamp(3rem, 6.5vw, 6.5rem)", color: "var(--gold-2)", letterSpacing: "-0.03em" }}>
                  {s.value}
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.38em]" style={{ color: "var(--text-2)" }}>
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Chapter>
    </div>
  );
}

function MosaicFrame({ images, id }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const gridY  = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const gridOp = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  return (
    <div ref={ref} className="w-full">
      <Chapter id={id}>
        <motion.div
          style={{ y: gridY, opacity: gridOp }}
          className="relative z-10 w-full max-w-[1500px] mx-auto px-6 md:px-14"
        >
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.42em] mb-10" style={{ color: "var(--gold)" }}>
            <div className="h-px w-10" style={{ background: "var(--gold)" }} />
            <span>Chapter Three</span>
            <span style={{ color: "var(--text-3)" }}>· The portfolio</span>
          </div>
          <h2 className="font-display leading-[0.95] mb-14" style={{ fontSize: "clamp(2.4rem, 5.4vw, 5rem)", letterSpacing: "-0.03em" }}>
            Eleven disciplines. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>One crew.</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {images.slice(0, 8).map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ amount: 0.3 }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-[3/4] overflow-hidden"
                style={{ borderRadius: "var(--r-lg)" }}
              >
                <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.75) 100%)" }} />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Chapter>
    </div>
  );
}

function ClosingFrame() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -20]);
  const smooth = useSpring(y, { stiffness: 60, damping: 20 });

  return (
    <div ref={ref} className="w-full">
      <Chapter id="closing">
        <motion.div style={{ y: smooth }} className="w-full max-w-[1400px] mx-auto px-6 md:px-14 relative z-10 text-center">
          <div className="flex items-center justify-center gap-3 font-mono text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--gold)" }}>
            <div className="h-px w-10" style={{ background: "var(--gold)" }} />
            <span>Chapter Nine</span>
            <span style={{ color: "var(--text-3)" }}>· Fin</span>
            <div className="h-px w-10" style={{ background: "var(--gold)" }} />
          </div>
          <h2 className="mt-10 font-display leading-[0.9]" style={{ fontSize: "clamp(3rem, 8vw, 9rem)", letterSpacing: "-0.035em" }}>
            The standard is <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>set.</span>
          </h2>
          <p className="mt-10 max-w-2xl mx-auto font-body text-lg md:text-xl" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
            Twelve seconds to price. Twenty-four hours to service. One vendor for the entire exterior of your property.
          </p>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-5">
            <Link
              to="/pricing"
              data-testid="reel-cta-plans"
              data-cursor="hover"
              data-cursor-label="Instant"
              className="btn-glow"
            >
              See Instant Plans <ArrowUpRight size={13} />
            </Link>
            <Link
              to="/audit"
              data-testid="reel-cta-audit"
              data-cursor="hover"
              className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em] transition-colors duration-500"
              style={{ color: "var(--text-2)" }}
            >
              <span className="inline-flex items-center justify-center w-10 h-10" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)" }}>
                <ArrowUpRight size={12} style={{ color: "var(--gold)" }} />
              </span>
              Book a walk-through
            </Link>
          </div>
          <div className="mt-24 font-mono text-[9px] uppercase tracking-[0.5em]" style={{ color: "var(--text-3)" }}>
            Bespoke Exterior Solutions · Reel 001 · Winter 2026
          </div>
        </motion.div>
      </Chapter>
    </div>
  );
}

/* ----- Progress rail ------------------------------------------------- */

function ProgressRail() {
  const { scrollYProgress } = useScroll();
  const width = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <motion.div
      aria-hidden
      className="fixed top-0 left-0 h-[2px] z-[60] pointer-events-none"
      style={{ width, background: "linear-gradient(90deg, var(--gold-3), var(--gold), var(--gold-2))", boxShadow: "0 0 12px var(--gold-2)" }}
    />
  );
}

/* ----- Page --------------------------------------------------------- */

export default function Reel() {
  const media = useMedia();

  // Chapter picks (fall back to any available image if the specific service isn't loaded yet)
  const pick = (id) => media[id] || Object.values(media)[0];
  const mosaicImages = [
    media["window-cleaning"],
    media["awning-cleaning"],
    media["sidewalk-cleaning"],
    media["graffiti-removal"],
    media["dumpster-cleaning"],
    media["parking-lot-sweeping"],
    media["outdoor-lighting"],
    media["pest-control"],
  ].filter(Boolean);

  useEffect(() => {
    // Force page background to true matte black for full cinema feel
    document.body.style.background = "#020202";
    return () => { document.body.style.background = ""; };
  }, []);

  return (
    <div className="relative" style={{ background: "#020202" }} data-testid="reel-page">
      <ProgressRail />

      <ColdOpen />

      <StillFrame
        id="the-standard"
        chapter="Chapter One"
        kicker="The standard"
        image={pick("pressure-washing")}
        headline={<>We are not the <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>cheapest.</span></>}
        subline="We are the last vendor you will need. Every property leaves our care documented, photographed, and warranted."
      />

      <StillFrame
        id="the-precision"
        chapter="Chapter Two"
        kicker="The precision"
        image={pick("window-cleaning")}
        reverse
        headline={<>Streak-free. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Every pane.</span></>}
        subline="Deionised water-fed poles up to five stories. Squeegee finish where a mirror is required. No smears, no callbacks, no exceptions."
      />

      <MosaicFrame id="portfolio" images={mosaicImages} />

      <StillFrame
        id="the-storm"
        chapter="Chapter Four"
        kicker="Storm response"
        image={pick("snow-removal")}
        headline={<>Twenty-four to seventy-two <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>hours.</span></>}
        subline="Weather event within the window of your scheduled visit? We are back. No invoice, no argument — the re-clean is priced in."
      />

      <StatFrame
        id="the-trust"
        chapter="Chapter Five"
        kicker="The receipt"
        image={pick("outdoor-lighting")}
        stats={[
          { value: "$2M", label: "General liability" },
          { value: "24 hr", label: "Quote turnaround" },
          { value: "11", label: "Disciplines under one COI" },
        ]}
      />

      <StillFrame
        id="the-line"
        chapter="Chapter Six"
        kicker="The line"
        image={pick("parking-lot-striping")}
        reverse
        headline={<>Chalk-line <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>straight.</span></>}
        subline="Sealcoat, prime, stripe. Two-coat epoxy paint dries in ninety minutes. Handicap symbols and directional arrows to municipal spec."
      />

      <StillFrame
        id="the-crew"
        chapter="Chapter Seven"
        kicker="The crew"
        image={pick("dumpster-cleaning")}
        headline={<>Veteran <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>owned.</span></>}
        subline="Bespoke was founded by a US Army veteran in 2013. Every technician carries the same discipline — punctual, uniformed, insured, documented."
      />

      <ClosingFrame />
    </div>
  );
}
